import type { EligibilityStatus, NormalizedNotice } from "../notices/types.js";
import { parseMoneyValue } from "../notices/amount.js";
import { normalizeComparableText } from "../notices/stable-text.js";
import type { CompanyProfile } from "./profile.js";

export interface EligibilityResult {
  status: EligibilityStatus;
  reasons: string[];
  requiredConfirmations: string[];
  eligibleForPriority: boolean;
}

const COMPANY_TYPES = ["중소기업", "벤처기업", "소공인", "소상공인", "중견기업", "대기업", "비영리기관", "연구기관"];

function regionRequirements(text: string): string[] {
  return [...text.matchAll(/([가-힣]{2,}(?:특별자치도|특별자치시|광역시|특별시|도|시|군|구))\s*(?:내|에)?\s*(?:소재|본사|사업장)/gu)]
    .map((match) => match[1]);
}

function companyTypeEvidence(text: string, profile: CompanyProfile): { recognized: boolean; passed: boolean; reason?: string } {
  const mentioned = COMPANY_TYPES.filter((type) => text.includes(type));
  if (mentioned.length === 0) return { recognized: false, passed: true };
  for (const type of profile.company.types) {
    if (new RegExp(`${type.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&")}\\s*(?:은|는|을|를)?\\s*제외`, "u").test(text)) {
      return { recognized: true, passed: false, reason: `${type} 제외 조건에 해당합니다.` };
    }
  }
  const eligibleMentioned = mentioned.filter((type) => !new RegExp(`${type}[^.]{0,8}제외`, "u").test(text));
  const match = eligibleMentioned.find((type) => profile.company.types.includes(type));
  if (match) return { recognized: true, passed: true, reason: `회사 유형 '${match}'이 지원대상 표현과 일치합니다.` };
  if (/대상|한함|이어야|기업$/u.test(text) && eligibleMentioned.length > 0) {
    return { recognized: true, passed: false, reason: `요구 기업유형(${eligibleMentioned.join(", ")})과 회사 유형이 일치하지 않습니다.` };
  }
  return { recognized: true, passed: true, reason: "기업유형은 원문 세부 조건 확인이 필요합니다." };
}

export function evaluateEligibility(
  notice: Pick<NormalizedNotice, "eligibilityText" | "summary" | "applicationEndAt">,
  profile: CompanyProfile,
  asOf: Date | string = new Date(),
): EligibilityResult {
  const text = normalizeComparableText(`${notice.eligibilityText} ${notice.summary}`);
  const reasons: string[] = [];
  const requiredConfirmations: string[] = [];
  const failures: string[] = [];
  let recognizedCriteria = 0;

  const typeResult = companyTypeEvidence(text, profile);
  if (typeResult.recognized) {
    recognizedCriteria += 1;
    if (typeResult.reason) (typeResult.passed ? reasons : failures).push(typeResult.reason);
  }

  const requiredRegions = regionRequirements(text);
  if (requiredRegions.length > 0 && !text.includes("전국")) {
    recognizedCriteria += 1;
    const actualRegions = [profile.company.headquarters.province, profile.company.headquarters.city];
    const matched = requiredRegions.some((required) => actualRegions.some((actual) => actual.includes(required) || required.includes(actual)));
    (matched ? reasons : failures).push(
      matched
        ? `소재지(${actualRegions.join(" ")})가 지역 조건과 일치합니다.`
        : `요구 지역(${requiredRegions.join(", ")})과 본사 소재지가 일치하지 않습니다.`,
    );
  }

  const withinYears = text.match(/(?:창업|업력)\s*(\d+)\s*년\s*(?:이내|이하|미만)/u);
  if (withinYears) {
    recognizedCriteria += 1;
    const limit = Number(withinYears[1]);
    (profile.company.businessYears <= limit ? reasons : failures).push(
      profile.company.businessYears <= limit
        ? `업력 ${profile.company.businessYears}년이 ${limit}년 이내 조건을 충족합니다.`
        : `업력 ${profile.company.businessYears}년이 ${limit}년 이내 조건을 초과합니다.`,
    );
  }
  const atLeastYears = text.match(/업력\s*(\d+)\s*년\s*(?:이상|초과)/u);
  if (atLeastYears) {
    recognizedCriteria += 1;
    const minimum = Number(atLeastYears[1]);
    (profile.company.businessYears >= minimum ? reasons : failures).push(
      profile.company.businessYears >= minimum
        ? `업력 ${profile.company.businessYears}년이 ${minimum}년 이상 조건을 충족합니다.`
        : `업력 ${profile.company.businessYears}년이 ${minimum}년 이상 조건에 미달합니다.`,
    );
  }

  const revenue = text.match(/(?:연매출|매출액)\s*([^,.;]+?)\s*(이하|미만|이상|초과)/u);
  if (revenue) {
    const parsed = parseMoneyValue(revenue[1]);
    if (parsed?.amountKrw != null) {
      recognizedCriteria += 1;
      const upper = revenue[2] === "이하" || revenue[2] === "미만";
      const passed = upper
        ? profile.company.annualRevenueKrw <= parsed.amountKrw
        : profile.company.annualRevenueKrw >= parsed.amountKrw;
      (passed ? reasons : failures).push(passed ? "매출액 조건을 충족합니다." : "매출액 조건을 충족하지 않습니다.");
    } else requiredConfirmations.push("매출액 기준을 공고 원문에서 확인해야 합니다.");
  }

  const employee = text.match(/(?:상시근로자|종업원|직원)\s*(\d+)\s*명\s*(이하|미만|이상|초과)/u);
  if (employee) {
    recognizedCriteria += 1;
    const threshold = Number(employee[1]);
    const upper = employee[2] === "이하" || employee[2] === "미만";
    const passed = upper ? profile.company.employeeCount <= threshold : profile.company.employeeCount >= threshold;
    (passed ? reasons : failures).push(passed ? "인력 규모 조건을 충족합니다." : "인력 규모 조건을 충족하지 않습니다.");
  }

  if (/컨소시엄|공동수급|수요기관|수요처.*필수/u.test(text)) {
    recognizedCriteria += 1;
    if (profile.partnerships.length > 0) reasons.push("등록된 협력 후보가 있습니다.");
    else requiredConfirmations.push("컨소시엄 또는 수요처 확보 여부를 확인해야 합니다.");
  }
  if (/기준일|공고일.*기준|접수일.*기준/u.test(text)) {
    requiredConfirmations.push(`기업 현황은 제출 기준일에 재확인해야 합니다(프로필 확인일 ${profile.verifiedAt}).`);
  }

  if (failures.length > 0) {
    return { status: "불충족", reasons: [...reasons, ...failures], requiredConfirmations, eligibleForPriority: false };
  }
  if (recognizedCriteria === 0) {
    return {
      status: "미확인",
      reasons: ["구조화 가능한 신청자격 조건을 찾지 못했습니다."],
      requiredConfirmations: ["공고 원문과 첨부파일의 신청자격을 확인해야 합니다."],
      eligibleForPriority: true,
    };
  }
  if (requiredConfirmations.length > 0) {
    return { status: "조건부 충족", reasons, requiredConfirmations, eligibleForPriority: true };
  }
  const deadline = notice.applicationEndAt ? new Date(notice.applicationEndAt) : null;
  if (deadline && deadline < new Date(asOf)) requiredConfirmations.push("접수 마감 여부를 다시 확인해야 합니다.");
  return {
    status: requiredConfirmations.length > 0 ? "조건부 충족" : "충족",
    reasons,
    requiredConfirmations,
    eligibleForPriority: true,
  };
}
